/*! rb-consent-style-v1 — Consentmo cookie banner buttons in the theme's animated gradients.
 *
 * <csm-cookie-consent> renders into an open shadow root, so page CSS cannot reach its buttons
 * (see the note in assets/rb-age-gate.css). The only way in is a <style> inside that root.
 * Custom properties do inherit across the boundary, so the theme's gradient tokens are used
 * directly; @keyframes do not, so the flow animation is restated here.
 *
 * Buttons are matched on data-csm-button-type, which Consentmo sets itself; its class names are
 * minified hashes that change between app releases.
 */
(function () {
  'use strict';

  var STYLE_ID = 'rb-csm-style';
  var CSS = [
    '@keyframes rb-csm-flow { 0% { background-position: 200% 50%; } 100% { background-position: 0% 50%; } }',
    'button[data-csm-button-type="accept"],',
    'button[data-csm-button-type="do_not_sell"],',
    'button[data-csm-button-type="reject"],',
    'button[data-csm-button-type="deny"] {',
    '  background-color: transparent !important;',
    '  background-size: 200% 100% !important;',
    '  border-color: transparent !important;',
    '  color: #fff !important;',
    '  animation: rb-csm-flow var(--rb-gradient-flow-duration, 7s) linear infinite;',
    '}',
    'button[data-csm-button-type="accept"] {',
    '  background-image: var(--rb-green-gradient, linear-gradient(90deg, #037F42 0%, #0A9D57 25%, #22B870 50%, #0A9D57 75%, #037F42 100%)) !important;',
    '}',
    'button[data-csm-button-type="do_not_sell"],',
    'button[data-csm-button-type="reject"],',
    'button[data-csm-button-type="deny"] {',
    '  background-image: var(--rb-red-gradient, linear-gradient(90deg, #8F1420 0%, #C1121F 25%, #E5383B 50%, #C1121F 75%, #8F1420 100%)) !important;',
    '}',
    '@media (prefers-reduced-motion: reduce) {',
    '  button[data-csm-button-type] { animation: none !important; background-size: auto !important; }',
    '}',
    ':host([data-rb-csm-lock]) .csm-wrapper {',
    '  top: var(--rb-csm-top) !important;',
    '  bottom: auto !important;',
    '  left: 0 !important;',
    '  right: auto !important;',
    '  width: var(--rb-csm-w) !important;',
    '  height: var(--rb-csm-h) !important;',
    '  transform: translateZ(0) !important;',
    '  pointer-events: none !important;',
    '}',
    ':host([data-rb-csm-lock]) .csm-wrapper > * { pointer-events: auto; }'
  ].join('\n');

  /* rb-csm-no-jump-v1 — Consentmo focuses a control inside the banner when it opens, without
     preventScroll. Under rb-desktop-scale the banner sat at the bottom of the document, so that
     focus scrolled the shopper down to the footer. Focus inside the banner never scrolls. */
  var nativeFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (opts) {
    var root = this.getRootNode && this.getRootNode();
    if (root && root.host && root.host.localName === 'csm-cookie-consent') {
      var o = {};
      if (opts) for (var k in opts) o[k] = opts[k];
      o.preventScroll = true;
      return nativeFocus.call(this, o);
    }
    return nativeFocus.apply(this, arguments);
  };

  /* rb-csm-viewport-lock-v1 — same geometry as rb-tidio-viewport-lock-v1 in theme.liquid: the
     scaled <html> is the containing block for fixed children, so the banner's bottom:0 meant the
     bottom of the page. The host itself cannot be moved (an important :host rule pins it at 0×0),
     so the shadow's .csm-wrapper becomes a one-viewport box pinned to the visible window, and its
     translateZ(0) makes it the containing block for the banner. The wrapper passes clicks
     through; its children take them back. Mobile never scales. */
  function pageScale() {
    var root = document.documentElement;
    if (!root.classList.contains('rb-desktop-scale')) return 1;
    var s = parseFloat(root.style.getPropertyValue('--rb-desktop-scale'));
    return s > 0 ? s : 1;
  }

  function lockHost(host) {
    var s = pageScale();
    if (s === 1) {
      host.removeAttribute('data-rb-csm-lock');
      return;
    }
    var y = window.scrollY || document.documentElement.scrollTop || 0;
    host.setAttribute('data-rb-csm-lock', '1');
    host.style.setProperty('--rb-csm-top', y / s + 'px');
    host.style.setProperty('--rb-csm-w', window.innerWidth / s + 'px');
    host.style.setProperty('--rb-csm-h', window.innerHeight / s + 'px');
  }

  var lockTick = 0;
  function lockAll() {
    lockTick = 0;
    var hosts = document.querySelectorAll('csm-cookie-consent');
    for (var i = 0; i < hosts.length; i++) lockHost(hosts[i]);
  }
  function scheduleLock() {
    if (!lockTick) lockTick = window.requestAnimationFrame(lockAll);
  }

  function styleRoot(root) {
    if (!root || root.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    root.appendChild(style);
  }

  function watchHost(host) {
    if (!host.shadowRoot) {
      /* The custom element may upgrade (and attach its root) after insertion. */
      var tries = 0;
      var poll = window.setInterval(function () {
        tries += 1;
        if (host.shadowRoot || tries > 40) {
          window.clearInterval(poll);
          if (host.shadowRoot) watchHost(host);
        }
      }, 250);
      return;
    }
    styleRoot(host.shadowRoot);
    if (host.__rbCsmWatched) return;
    host.__rbCsmWatched = true;
    /* Re-add the style if Consentmo re-renders its root and drops it. */
    new MutationObserver(function () {
      styleRoot(host.shadowRoot);
    }).observe(host.shadowRoot, { childList: true });
  }

  function scan() {
    var hosts = document.querySelectorAll('csm-cookie-consent');
    for (var i = 0; i < hosts.length; i++) {
      watchHost(hosts[i]);
      lockHost(hosts[i]);
    }
  }

  function start() {
    scan();
    window.addEventListener('scroll', scheduleLock, { passive: true });
    window.addEventListener('resize', scheduleLock);
    window.addEventListener('rb-desktop-scale', scheduleLock);
    /* The banner is mounted directly under <body> (and re-attached there by rb-cookie-hold.js),
       so watching body's own children is enough — no subtree observer on heavy pages. */
    new MutationObserver(scan).observe(document.body, { childList: true });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
